/** Shared contracts of the NFC writers (desktop bridge and Android plugin). */

/** Answer of POST /api/asset-tags/{id}/nfc-payload/. Contains the derived tag password: never log or persist it. */
export interface NfcPayload {
  url: string
  password_hex: string
  pack_hex: string
  auth0: number
}

export interface NfcWriteResult {
  uid: string
  alreadyProtected: boolean
}

/** Codes shared with the bridge and the Android plugin, plus two client-side ones. */
export type NfcErrorCode =
  | 'NO_READER'
  | 'NO_TAG'
  | 'TAG_LOST'
  | 'UNSUPPORTED_TAG'
  | 'AUTH_FAILED'
  | 'TIMEOUT'
  | 'NO_NFC'
  | 'NFC_DISABLED'
  | 'INVALID_REQUEST'
  | 'BRIDGE_UNAVAILABLE'
  | 'UNKNOWN'

export const NFC_ERROR_CODES: readonly NfcErrorCode[] = [
  'NO_READER', 'NO_TAG', 'TAG_LOST', 'UNSUPPORTED_TAG', 'AUTH_FAILED', 'TIMEOUT', 'NO_NFC', 'NFC_DISABLED',
  'INVALID_REQUEST', 'BRIDGE_UNAVAILABLE', 'UNKNOWN',
]

export function toErrorCode(value: unknown): NfcErrorCode {
  return typeof value === 'string' && (NFC_ERROR_CODES as readonly string[]).includes(value) ? (value as NfcErrorCode) : 'UNKNOWN'
}

export class NfcWriteError extends Error {
  readonly code: NfcErrorCode

  constructor(code: NfcErrorCode, message?: string) {
    super(message ?? code)
    this.name = 'NfcWriteError'
    this.code = code
  }
}

/** The bridge is not running / the Android plugin is missing: the writer cannot be reached at all. */
export class BridgeUnavailableError extends Error {
  constructor(message = 'NFC writer unavailable') {
    super(message)
    this.name = 'BridgeUnavailableError'
  }
}

export type NfcWriterStatus =
  | { state: 'no_reader' }
  | { state: 'no_tag' }
  | { state: 'tag_present'; uid?: string; protectedTag?: boolean | null }
  | { state: 'ready' } // Android: nothing to pre-detect, the tag is tapped during the write
  | { state: 'no_nfc' }
  | { state: 'nfc_disabled' }

export interface NfcWriter {
  kind: 'bridge' | 'android'
  /** Throws BridgeUnavailableError when the writer itself cannot be reached. */
  getStatus(signal?: AbortSignal): Promise<NfcWriterStatus>
  /** Throws NfcWriteError. */
  write(payload: NfcPayload, signal?: AbortSignal): Promise<NfcWriteResult>
}
